import React from 'react';
import { Badge } from '../../design-system';
import PromoCountdown from './PromoCountdown';
import './courseKit.css';

interface AppliedPromoCodeProps {
  /** The code the server confirmed for this visit, e.g. from ?promo=CODE. */
  code: string | null | undefined;
  /** The code's end date from the promotion row, or null when it has none. */
  endsAt?: Date | string | null;
  /** Off when another live countdown to the same deadline is already on screen. */
  showCountdown?: boolean;
  className?: string;
}

/**
 * Names a promo code that arrived in a link and is priced into the page.
 *
 * WHY: Rob, 2026-10-17. A past student opened the Bootcamp from an email link
 * with ?promo=PASTSTUDENT66 and the page silently showed the discounted price.
 * Nothing said a code was applied, that it was a special offer, or when it
 * runs out. The buyer needs to see all three before deciding.
 *
 * Every value comes from the catalog the server priced with the code. When the
 * code lookup fails the hook reports no applied code and this renders nothing:
 * never a guessed code, never a countdown without a real end date.
 */
export default function AppliedPromoCode({
  code,
  endsAt = null,
  showCountdown = true,
  className,
}: AppliedPromoCodeProps): React.ReactElement | null {
  if (!code) return null;
  return (
    <div
      className={['ck-applied-code', className].filter(Boolean).join(' ')}
      data-testid="applied-promo-code"
    >
      <p className="ck-applied-code__line">
        <Badge variant="warning">Special offer</Badge>
        <span className="ck-applied-code__text">
          Code{' '}
          <strong className="ck-applied-code__name" data-testid="applied-promo-code-name">
            {code}
          </strong>{' '}
          applied from your link
        </span>
      </p>
      {showCountdown && endsAt ? (
        // Compact clock (10d 04:59:44) so the price stays above the fold on a phone.
        <PromoCountdown endsAt={endsAt} label="Code ends in" variant="clock" />
      ) : null}
    </div>
  );
}
